// https://observablehq.com/@d3/zoomable-sunburst@353
import { textSplitter } from '@/utils/app';

export default function define(runtime, observer) {
  const main = runtime.module();

  main
    .variable(observer('chart'))
    .define(
      'chart',
      ['partition', 'data', 'd3', 'width', 'color', 'arc', 'format', 'radius'],
      function (partition, data, d3, width, color, arc, format, radius) {
        const root = partition(data);

        window.emitter.emit('sunburst:setTitle', { root, item: null });

        root.each((d) => (d.current = d));

        const svg = d3
          .create('svg')
          .attr('viewBox', [0, 0, width, width])
          .style('font-family', 'Raleway, sans-serif')
          .style('font-size', '14px');

        const g = svg
          .append('g')
          .attr('transform', `translate(${width / 2}, ${width / 2})`);

        const path = g
          .append('g')
          .selectAll('path')
          .data(root.descendants().slice(1))
          .join('path')
          .attr('fill', (d) => {
            return d.data.color || '#00ff00';
          })
          .attr('fill-opacity', (d) =>
            arcVisible(d.current) ? (d.children ? 0.6 : 0.4) : 0,
          )
          .attr('d', (d) => arc(d.current));

        path
          .filter((d) => d.children)
          .style('cursor', 'pointer')
          .on('click', clicked);

        path.append('title').text(
          (d) =>
            `${d
              .ancestors()
              .map((d) => d.data.name)
              .reverse()
              .join('/')}\n${format(d.value)}`,
        );

        const label = g
          .append('g')
          .attr('pointer-events', 'none')
          .attr('text-anchor', 'middle')
          .style('user-select', 'none')
          .selectAll('text')
          .data(root.descendants().slice(1))
          .join('text');

        label
          .selectAll('tspan')
          .data((d) => {
            return textSplitter(d.data.name, 15);
          })
          .enter()
          .append('tspan')
          .attr('x', '0')
          .attr('dy', (d, i, v) => {
            const length = v.length;
            if (length === 1) {
              return '0.35em';
            } else if (length > 1) {
              return i ? '1.30em' : `${(0.2 + (length - 2) * 0.7) * -1}em`;
            }
          })
          .text((d) => d);

        label
          .attr('fill-opacity', (d) => +labelVisible(d.current))
          .attr('transform', (d) => labelTransform(d.current))
          .attr('font-weight', 'bold')
          .attr('letter-spacing', '1');

        const parent = g
          .append('circle')
          .datum(root)
          .attr('r', radius)
          .attr('fill', 'none')
          .attr('pointer-events', 'all')
          .style('cursor', 'pointer')
          .on('click', clicked);

        function clicked(event, p) {
          window.emitter.emit('sunburst:setTitle', { root, item: p });

          parent.datum(p.parent || root);

          root.each(
            (d) =>
              (d.target = {
                x0:
                  Math.max(0, Math.min(1, (d.x0 - p.x0) / (p.x1 - p.x0))) *
                  2 *
                  Math.PI,
                x1:
                  Math.max(0, Math.min(1, (d.x1 - p.x0) / (p.x1 - p.x0))) *
                  2 *
                  Math.PI,
                y0: Math.max(0, d.y0 - p.depth),
                y1: Math.max(0, d.y1 - p.depth),
              }),
          );

          const t = g.transition().duration(750);

          // Transition the data on all arcs, even the ones that aren’t visible,
          // so that if this transition is interrupted, entering arcs will start
          // the next transition from the desired position.
          path
            .transition(t)
            .tween('data', (d) => {
              const i = d3.interpolate(d.current, d.target);
              return (t) => (d.current = i(t));
            })
            .filter(function (d) {
              return +this.getAttribute('fill-opacity') || arcVisible(d.target);
            })
            .attr('fill-opacity', (d) =>
              arcVisible(d.target) ? (d.children ? 0.6 : 0.4) : 0,
            )
            .attrTween('d', (d) => () => arc(d.current));

          label
            .filter(function (d) {
              return (
                +this.getAttribute('fill-opacity') || labelVisible(d.target)
              );
            })
            .transition(t)
            .attr('fill-opacity', (d) => +labelVisible(d.target))
            .attrTween('transform', (d) => () => labelTransform(d.current));
        }

        function arcVisible(d) {
          return d.y1 <= 3 && d.y0 >= 1 && d.x1 > d.x0;
        }

        function labelVisible(d) {
          return d.y1 <= 3 && d.y0 >= 1 && (d.y1 - d.y0) * (d.x1 - d.x0) > 0.03;
        }

        function labelTransform(d) {
          const x = (((d.x0 + d.x1) / 2) * 180) / Math.PI;
          const y = ((d.y0 + d.y1) / 2) * radius;
          return `rotate(${x - 90}) translate(${y},0) rotate(${
            x < 180 ? 0 : 180
          })`;
        }

        return svg.node();
      },
    );

  main
    .variable(observer('data'))
    .define('data', ['FileAttachment'], function () {
      const sunburstElement = document.querySelector('[data-sunburst="true"]');
      const sunburstLanguage = sunburstElement.dataset.sunburstLanguage;
      console.log(sunburstLanguage);

      if (sunburstLanguage === 'fr') {
        return require('@/data/sunburstSurfaceData.json');
      }
      if (sunburstLanguage === 'de') {
        return require('@/data/sunburstSurfaceDataDE.json');
      }

      return require('@/data/sunburstSurfaceDataEN.json');
    });

  main
    .variable(observer('partition'))
    .define('partition', ['d3'], function (d3) {
      return (data) => {
        const root = d3
          .hierarchy(data)
          .sum((d) => d.value)
          .sort((a, b) => b.value - a.value);
        return d3.partition().size([2 * Math.PI, root.height + 1])(root);
      };
    });

  main
    .variable(observer('color'))
    .define('color', ['d3', 'data'], function (d3, data) {
      return d3.scaleOrdinal(
        d3.quantize(d3.interpolateRainbow, data.children.length + 1),
      );
    });

  main.variable(observer('format')).define('format', ['d3'], function (d3) {
    return d3.format(',d');
  });

  main.variable(observer('width')).define('width', function () {
    return 1000;
  });

  main
    .variable(observer('radius'))
    .define('radius', ['width'], function (width) {
      return width / 6;
    });

  main
    .variable(observer('arc'))
    .define('arc', ['d3', 'radius'], function (d3, radius) {
      return d3
        .arc()
        .startAngle((d) => d.x0)
        .endAngle((d) => d.x1)
        .padAngle((d) => Math.min((d.x1 - d.x0) / 2, 0.005))
        .padRadius(radius * 1.5)
        .innerRadius((d) => d.y0 * radius)
        .outerRadius((d) => Math.max(d.y0 * radius, d.y1 * radius - 1));
    });

  main.variable(observer('d3')).define('d3', ['require'], function (require) {
    return require('d3@6');
  });

  return main;
}
